import { useAppSelector } from "@/hooks/useAppSelector";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, ComposedChart, Line, Legend } from "recharts";

export const PriceIncreasesGraph = () => {
  const { events } = useAppSelector(state => state.events);

  // Process events data to count price increases and total events by quarter
  const quarterStats = events
    .filter(event => !event.error) // Filter out events with errors
    .reduce((acc: { 
      [key: string]: { increases: number; total: number } 
    }, event) => {
      // Extract year and month from v1 (format: YYYYMM)
      const year = event.v1.substring(0, 4);
      const month = parseInt(event.v1.substring(4, 6));
      const quarter = `Q${Math.ceil(month / 3)} ${year}`;

      // Only include quarters from Q1 2023 to Q4 2024
      const quarterYear = parseInt(year);
      if (quarterYear < 2023 || quarterYear > 2024) return acc;

      // Initialize quarter stats if not exists
      if (!acc[quarter]) {
        acc[quarter] = { increases: 0, total: 0 };
      }

      // Count total events and price increases
      acc[quarter].total += 1;
      if (event.priceIncreased) {
        acc[quarter].increases += 1;
      }

      return acc;
    }, {});

  // Create array of all quarters from Q1 2023 to Q4 2024
  const allQuarters = [];
  for (let year = 2023; year <= 2024; year++) {
    for (let quarter = 1; quarter <= 4; quarter++) {
      allQuarters.push(`Q${quarter} ${year}`);
    }
  }

  // Convert to array format for Recharts, including all quarters
  const chartData = allQuarters.map(quarter => {
    const stats = quarterStats[quarter] || { increases: 0, total: 0 };
    const percentage = stats.total > 0 ? (stats.increases / stats.total) * 100 : 0;
    
    return {
      quarter,
      increases: stats.increases,
      percentage: Number(percentage.toFixed(1))
    };
  });

  return (
    <div className="grid grid-cols-2 gap-8">
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-black">Price Increases by Quarter</h3>
        <div className="flex flex-col items-start w-full">
          <div className="w-full h-[300px]">
            <ChartContainer
              config={{
                bar: {
                  theme: {
                    light: "#ef4444",
                    dark: "#ef4444",
                  },
                },
              }}
            >
              <BarChart 
                data={chartData} 
                margin={{ top: 20, right: 30, left: 30, bottom: 60 }}
              >
                <XAxis
                  dataKey="quarter"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fill: '#000000', fontSize: 12 }}
                  stroke="#000000"
                />
                <YAxis
                  tick={{ fill: '#000000', fontSize: 12 }}
                  stroke="#000000"
                  label={{ 
                    value: 'Number of Increases', 
                    angle: -90, 
                    position: 'insideLeft', 
                    fill: '#000000'
                  }}
                />
                <Bar 
                  dataKey="increases" 
                  fill="#ef4444"
                  radius={[4, 4, 0, 0]} 
                />
                <ChartTooltip />
              </BarChart>
            </ChartContainer>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-black">Percentage of Companies with Price Increases</h3>
        <div className="flex flex-col items-start w-full">
          <div className="w-full h-[300px]">
            <ChartContainer
              config={{
                line: {
                  theme: {
                    light: "#2563eb",
                    dark: "#2563eb",
                  },
                },
              }}
            >
              <ComposedChart 
                data={chartData} 
                margin={{ top: 20, right: 30, left: 30, bottom: 60 }}
              >
                <XAxis
                  dataKey="quarter"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fill: '#000000', fontSize: 12 }}
                  stroke="#000000"
                />
                <YAxis
                  tick={{ fill: '#000000', fontSize: 12 }}
                  stroke="#000000"
                  label={{ 
                    value: 'Percentage (%)', 
                    angle: -90, 
                    position: 'insideLeft', 
                    fill: '#000000'
                  }}
                />
                <Line 
                  type="monotone"
                  dataKey="percentage"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={{ fill: '#2563eb' }}
                />
                <ChartTooltip />
              </ComposedChart>
            </ChartContainer>
          </div>
        </div>
      </div>
    </div>
  );
};